import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class DashboardErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Dashboard error caught:', error, errorInfo);
  }

  private handleRefresh = async () => {
    try {
      // Attempt to refresh the session
      await supabase.auth.refreshSession();
      
      // Force reload page
      window.location.reload();
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }

  private handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
      window.location.href = '/login';
    }
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <Card className="w-full mx-auto my-12 max-w-md border-red-200 shadow-md">
          <CardHeader className="bg-red-50">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Dashboard Data Error
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-700 mb-4">
              There was a problem loading the dashboard data. This could be due to an authentication issue or a server problem.
            </p>
            <p className="text-xs text-gray-500 mb-2">
              Error: {this.state.error?.message || 'Unknown error'}
            </p>
          </CardContent>
          <CardFooter className="flex justify-between bg-gray-50">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={this.handleRefresh}
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button 
              variant="destructive"
              onClick={this.handleLogout}
            >
              Log Out
            </Button>
          </CardFooter>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default DashboardErrorBoundary; 